"use client";

import { useState, useEffect, useCallback } from "react";
import dynamic from "next/dynamic";
import {
  Users,
  Receipt,
  UserPlus,
  Trophy,
  Radio,
  Siren,
  DollarSign,
  Bot,
  Loader2,
  AlertCircle,
  RefreshCw,
  MapPin,
  TrendingUp,
  TrendingDown,
  Target,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

const DashboardMap = dynamic(() => import("./dashboard-map"), { ssr: false });

interface DashboardSummary {
  cobranca: {
    totalInadimplente: number;
    totalClientes: number;
    totalBaseAtiva: number;
    percentualBase: number;
    valorRecuperadoMes: number;
  };
  suporte: {
    alertasAtivos: number;
    massivasAbertas: number;
    resolvidosHoje: number;
    resolvidosOntem: number;
  };
  comercial: {
    totalLeads: number;
    leadsHoje: number;
    leadsOntem: number;
    ganhosHoje: number;
    ganhosOntem: number;
    taxaConversao: number;
    pipelineTotal: number;
    metaVendas: number;
    vendasMes: number;
    metaFaturamento: number;
    faturamentoMes: number;
  };
  ia: {
    custoMes: number;
    totalRequests: number;
    previsaoMensal: number;
    configurado: boolean;
  };
  atividadesRecentes: Array<{
    tipo: "comercial" | "suporte" | "ia";
    descricao: string;
    timestamp: string;
  }>;
}

// Shape real da resposta de `/dashboard/summary`: cada módulo usa
// `.catch(() => null)` no backend (um módulo fora do ar não derruba os
// outros) — `DashboardSummary` acima é a versão já normalizada (sem null),
// usada pelo resto do arquivo pra não espalhar `?.`/fallback em toda parte.
type DashboardSummaryRaw = {
  [K in keyof DashboardSummary]: DashboardSummary[K] | null;
};

// O backend devolve cada módulo com `.catch(() => null)` (um módulo fora do
// ar não derruba os outros) — sem isto, `d.comercial.leadsHoje` com
// `comercial: null` quebra a página inteira em vez de só aquele card ficar
// zerado. `atividadesRecentes` já cai em `[]` no service, não precisa aqui.
function normalizeSummary(raw: DashboardSummaryRaw): DashboardSummary {
  return {
    cobranca: raw.cobranca ?? {
      totalInadimplente: 0,
      totalClientes: 0,
      totalBaseAtiva: 0,
      percentualBase: 0,
      valorRecuperadoMes: 0,
    },
    suporte: raw.suporte ?? {
      alertasAtivos: 0,
      massivasAbertas: 0,
      resolvidosHoje: 0,
      resolvidosOntem: 0,
    },
    comercial: raw.comercial ?? {
      totalLeads: 0,
      leadsHoje: 0,
      leadsOntem: 0,
      ganhosHoje: 0,
      ganhosOntem: 0,
      taxaConversao: 0,
      pipelineTotal: 0,
      metaVendas: 0,
      vendasMes: 0,
      metaFaturamento: 0,
      faturamentoMes: 0,
    },
    ia: raw.ia ?? {
      custoMes: 0,
      totalRequests: 0,
      previsaoMensal: 0,
      configurado: false,
    },
    atividadesRecentes: raw.atividadesRecentes ?? [],
  };
}

function calcTrend(hoje: number, ontem: number): number | undefined {
  if (ontem === 0 && hoje === 0) return undefined;
  if (ontem === 0) return 100;
  return Math.round(((hoje - ontem) / ontem) * 100);
}

interface PropostaMapaItem {
  id: string;
  bairro: string;
  cidade: string | null;
  totalReclamacoes: number;
  mapLat: number | null;
  mapLng: number | null;
}

interface MassivaMapa {
  id: string;
  bairros: string[];
  motivo: string;
  tipo: string;
  origem: string;
  status: string;
  totalConexoes: number;
  polygonCoords: number[][];
}

interface AlertaAtivo {
  id: string;
  bairro: string;
  cidade: string | null;
  assunto: string;
  totalTickets: number;
  isMassiva: boolean;
  status: string;
  createdAt: string;
}

const TIPO_COLORS: Record<string, string> = {
  comercial: "#2E86C1",
  suporte: "#F59E0B",
  ia: "#8B5CF6",
};

function formatBRL(value: number): string {
  return value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function formatNumber(value: number): string {
  return value.toLocaleString("pt-BR");
}

function ProgressRing({
  value,
  max,
  label,
  display,
  color,
}: {
  value: number;
  max: number;
  label: string;
  display: string;
  color: string;
}) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  const radius = 54;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (pct / 100) * circumference;

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative h-32 w-32">
        <svg className="h-full w-full -rotate-90" viewBox="0 0 128 128">
          <circle
            cx="64"
            cy="64"
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth="8"
            className="text-muted/30"
          />
          <circle
            cx="64"
            cy="64"
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            style={{ transition: "stroke-dashoffset 0.8s ease" }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-lg font-bold text-foreground">{Math.round(pct)}%</span>
        </div>
      </div>
      <div className="text-center">
        <p className="text-sm font-medium text-foreground">{label}</p>
        <p className="text-xs text-muted-foreground">{display}</p>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const [data, setData] = useState<DashboardSummary | null>(null);
  const [massivas, setMassivas] = useState<MassivaMapa[]>([]);
  const [propostas, setPropostas] = useState<PropostaMapaItem[]>([]);
  const [alertasAtivos, setAlertasAtivos] = useState<AlertaAtivo[]>([]);
  const [metaProgresso, setMetaProgresso] = useState<{ meta: number; atual: number; percentual: number; status: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [summary, massivasData, alertasData] = await Promise.all([
        api.request<DashboardSummaryRaw>("/dashboard/summary"),
        api.request<MassivaMapa[]>("/suporte/massivas/ativas-mapa").catch(() => [] as MassivaMapa[]),
        api.request<AlertaAtivo[]>("/suporte/alertas/ativos").catch(() => [] as AlertaAtivo[]),
      ]);

      setData(normalizeSummary(summary));
      setMassivas(massivasData);
      setAlertasAtivos(alertasData);

      // Propostas de massiva (sinal da IA) pendentes — marcador âmbar no mapa
      const propostasData = await api
        .request<{ pendentes: PropostaMapaItem[] }>("/suporte/massivas/propostas")
        .then((r) => r.pendentes)
        .catch(() => [] as PropostaMapaItem[]);
      setPropostas(propostasData);

      // v1.0 Comercial: metas ocultas — não busca progresso de meta (o card cai no fallback de Alertas Ativos). Reversível.
      // try {
      //   const mesAtual = new Date().toISOString().slice(0, 7);
      //   const metas = await api.request<Array<{ meta: number; atual: number; percentual: number; status: string; tipo: string; vendedorErpId: number | null }>>(
      //     `/comercial/metas-v2/progresso?mes=${mesAtual}`
      //   );
      //   const metaGlobal = metas.find((m) => m.vendedorErpId === null && m.tipo === "VENDAS")
      //     || metas.find((m) => m.tipo === "VENDAS")
      //     || (metas.length > 0 ? metas[0] : null);
      //   setMetaProgresso(metaGlobal);
      // } catch {
      //   // sem meta configurada
      // }
      void setMetaProgresso; // mantido p/ reversão (metas ocultas na v1.0)
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Erro ao carregar dashboard";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-96 flex-col items-center justify-center gap-4">
        <AlertCircle className="h-10 w-10 text-destructive" />
        <p className="text-muted-foreground">{error}</p>
        <Button variant="outline" onClick={fetchData}>
          Tentar novamente
        </Button>
      </div>
    );
  }

  const d = data!;
  const user = api.getUser();
  const firstName = user?.name?.split(" ")[0] || "Usuario";
  const initials = user?.name
    ? user.name.split(" ").map((n: string) => n[0]).join("").slice(0, 2).toUpperCase()
    : "GK";
  const avatarSrc = user?.avatarUrl
    ? `${process.env.NEXT_PUBLIC_API_URL || "/api"}${user.avatarUrl}`
    : null;

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Bom dia" : hour < 18 ? "Boa tarde" : "Boa noite";

  return (
    <div className="space-y-6">
      {/* Saudacao personalizada */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-full ring-2 ring-primary/20">
            {avatarSrc ? (
              <img src={avatarSrc} alt={firstName} className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-primary/10 text-lg font-bold text-primary">
                {initials}
              </div>
            )}
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              {greeting}, {firstName}! <span className="inline-block animate-bounce">👋</span>
            </h1>
            <p className="text-sm text-muted-foreground">
              Aqui está o resumo da sua operação hoje
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData}>
          <RefreshCw className="mr-2 h-4 w-4" />
          Atualizar
        </Button>
      </div>

      {/* Row 1 — KPI Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiSimple
          icon={<Users className="h-5 w-5" />}
          iconBg="#2E86C1"
          title="Clientes Ativos"
          value={formatNumber(d.cobranca.totalBaseAtiva)}
          subtitle="na base"
        />
        <KpiSimple
          icon={<Receipt className="h-5 w-5" />}
          iconBg="#EF4444"
          title="Inadimplentes"
          value={formatNumber(d.cobranca.totalClientes)}
          subtitle={`${d.cobranca.percentualBase.toFixed(1)}% da base`}
        />
        <KpiSimple
          icon={<UserPlus className="h-5 w-5" />}
          iconBg="#10B981"
          title="Leads Hoje"
          value={formatNumber(d.comercial.leadsHoje)}
          subtitle="vs ontem"
          trend={calcTrend(d.comercial.leadsHoje, d.comercial.leadsOntem)}
        />
        <KpiSimple
          icon={<Trophy className="h-5 w-5" />}
          iconBg="#F59E0B"
          title="Conversões Hoje"
          value={formatNumber(d.comercial.ganhosHoje)}
          subtitle="vs ontem"
          trend={calcTrend(d.comercial.ganhosHoje, d.comercial.ganhosOntem)}
        />
      </div>

      {/* Row 2 — KPI Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {metaProgresso ? (
          <Card className="shadow-md">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-lg" style={{ backgroundColor: "#F59E0B20" }}>
                    <Target className="h-5 w-5" style={{ color: "#F59E0B" }} />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Meta Vendas</p>
                    <p className="text-lg font-bold tracking-tight">
                      {metaProgresso.atual}/{metaProgresso.meta}
                    </p>
                  </div>
                </div>
                <span className={`text-sm font-bold ${
                  metaProgresso.status === "BATEU" ? "text-emerald-500" :
                  metaProgresso.status === "NO_RITMO" ? "text-blue-500" :
                  metaProgresso.status === "ATENCAO" ? "text-amber-500" : "text-red-500"
                }`}>
                  {metaProgresso.percentual.toFixed(0)}%
                </span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className="h-2 rounded-full transition-all duration-500"
                  style={{
                    width: `${Math.min(metaProgresso.percentual, 100)}%`,
                    backgroundColor: metaProgresso.status === "BATEU" ? "#10B981" :
                      metaProgresso.status === "NO_RITMO" ? "#3B82F6" :
                      metaProgresso.status === "ATENCAO" ? "#F59E0B" : "#EF4444",
                  }}
                />
              </div>
            </CardContent>
          </Card>
        ) : (
          <KpiSimple
            icon={<Radio className="h-5 w-5" />}
            iconBg={d.suporte.alertasAtivos > 0 ? "#EF4444" : "#6B7489"}
            title="Alertas Ativos"
            value={formatNumber(d.suporte.alertasAtivos)}
            valueColor={d.suporte.alertasAtivos > 0 ? "#EF4444" : undefined}
          />
        )}
        <KpiSimple
          icon={<Siren className="h-5 w-5" />}
          iconBg={d.suporte.massivasAbertas > 0 ? "#EF4444" : "#6B7489"}
          title="Massivas Abertas"
          value={formatNumber(d.suporte.massivasAbertas)}
          valueColor={d.suporte.massivasAbertas > 0 ? "#EF4444" : undefined}
        />
        <KpiSimple
          icon={<DollarSign className="h-5 w-5" />}
          iconBg="#10B981"
          title="Recuperado (mês)"
          value={formatBRL(d.cobranca.valorRecuperadoMes)}
          subtitle="este mês"
        />
        <KpiSimple
          icon={<Bot className="h-5 w-5" />}
          iconBg="#8B5CF6"
          title="Custo IA (mês)"
          value={`$${d.ia.custoMes.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          subtitle="USD"
        />
      </div>

      {/* Row 3 — Mapa + Massivas/Reclamacoes */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Mapa Massivas */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <MapPin className="h-4 w-4" />
              Mapa — Massivas Abertas
            </CardTitle>
          </CardHeader>
          <CardContent>
            {massivas.length === 0 ? (
              <div className="flex h-72 items-center justify-center text-muted-foreground">
                Nenhuma massiva aberta
              </div>
            ) : (
              <div className="h-72 overflow-hidden rounded-lg">
                <DashboardMap massivas={massivas} alertas={alertasAtivos} propostas={propostas} />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Lista Massivas e Reclamacoes por Bairro */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Siren className="h-4 w-4" />
              Massivas e Reclamações
            </CardTitle>
          </CardHeader>
          <CardContent>
            {massivas.length === 0 && alertasAtivos.length === 0 ? (
              <div className="flex h-72 items-center justify-center text-muted-foreground">
                Nenhuma massiva ou reclamação ativa
              </div>
            ) : (
              <div className="h-72 space-y-2 overflow-y-auto pr-1">
                {/* Massivas primeiro */}
                {massivas.map((m) => (
                  <div
                    key={`m-${m.id}`}
                    className="flex items-center gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-3 transition-colors hover:bg-destructive/10"
                  >
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-destructive text-xs font-bold text-white">
                      {m.totalConexoes}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-foreground">
                        {m.bairros.slice(0, 3).join(", ")}
                        {m.bairros.length > 3 && ` +${m.bairros.length - 3}`}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {m.motivo} · MASSIVA
                      </p>
                    </div>
                    <span className="shrink-0 rounded-full bg-destructive/15 px-2 py-0.5 text-xs font-semibold text-destructive">
                      MASSIVA
                    </span>
                  </div>
                ))}
                {/* Alertas individuais */}
                {alertasAtivos.map((a) => (
                  <div
                    key={`a-${a.id}`}
                    className="flex items-center gap-3 rounded-lg border border-border/50 p-3 transition-colors hover:bg-muted/50"
                  >
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-warning/80 text-xs font-bold text-white">
                      {a.totalTickets}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-foreground">
                        {a.bairro}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {a.assunto}{a.cidade ? ` · ${a.cidade}` : ""}
                      </p>
                    </div>
                    <span className="shrink-0 rounded-full bg-warning/15 px-2 py-0.5 text-xs font-semibold text-warning">
                      {a.assunto}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Row 4 — Atividades Recentes */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Atividades Recentes</CardTitle>
        </CardHeader>
        <CardContent>
          {d.atividadesRecentes.length === 0 ? (
            <div className="flex h-24 items-center justify-center text-muted-foreground">
              Nenhuma atividade recente
            </div>
          ) : (
            <div className="space-y-3">
              {d.atividadesRecentes.slice(0, 10).map((atividade, i) => {
                const color = TIPO_COLORS[atividade.tipo] || "#64748b";
                const time = new Date(atividade.timestamp);
                const hhmm = `${String(time.getHours()).padStart(2, "0")}:${String(time.getMinutes()).padStart(2, "0")}`;
                return (
                  <div key={i} className="flex items-start gap-3">
                    <div
                      className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full"
                      style={{ backgroundColor: color }}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-foreground">{atividade.descricao}</p>
                    </div>
                    <span className="shrink-0 text-xs text-muted-foreground">{hhmm}</span>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function KpiSimple({
  icon,
  title,
  value,
  subtitle,
  valueColor,
  trend,
  iconBg,
}: {
  icon: React.ReactNode;
  title: string;
  value: string;
  subtitle?: string;
  valueColor?: string;
  trend?: number;
  iconBg?: string;
}) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-sm transition-all duration-200 hover:shadow-md hover:ring-1 hover:ring-foreground/10">
      <div className="mb-3 flex items-center justify-between">
        <span className="text-sm font-medium text-muted-foreground">{title}</span>
        <div
          className="flex h-9 w-9 items-center justify-center rounded-lg"
          style={{ backgroundColor: iconBg ? `${iconBg}15` : "var(--color-muted)", color: iconBg || "var(--color-muted-foreground)" }}
        >
          {icon}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <p
          className="text-2xl font-bold tracking-tight"
          style={{ color: valueColor || "var(--color-foreground)" }}
        >
          {value}
        </p>
        {trend !== undefined && (
          <div
            className={`flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-xs font-medium ${
              trend >= 0
                ? "bg-green-50 text-green-700 dark:bg-success/15 dark:text-success"
                : "bg-red-50 text-red-700 dark:bg-destructive/15 dark:text-destructive"
            }`}
          >
            {trend >= 0 ? (
              <TrendingUp className="h-3 w-3" />
            ) : (
              <TrendingDown className="h-3 w-3" />
            )}
            {trend >= 0 ? "+" : ""}{trend}%
          </div>
        )}
      </div>
      {subtitle && (
        <p className="mt-1 text-xs text-muted-foreground">{subtitle}</p>
      )}
    </div>
  );
}
